import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Shield, ArrowLeft, Users, Globe, MessageCircle, Loader2, KeyRound } from "lucide-react";
import { WebhookSecretsCard } from "@/components/admin/WebhookSecretsCard";

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    if (!user.is_admin) { setLoadingData(false); return; }
    (async () => {
      try {
        const [u, b, t] = await Promise.all([
          api.get("/admin/users"), api.get("/admin/businesses"), api.get("/admin/support")
        ]);
        setUsers(u.data); setBusinesses(b.data); setTickets(t.data);
      } catch { toast.error("Failed to load"); }
      setLoadingData(false);
    })();
  }, [user, loading, navigate]);

  const toggleStatus = async (biz) => {
    const next = biz.status === "disabled" ? "draft" : (biz.status === "published" ? "disabled" : "published");
    try {
      await api.put(`/admin/business/${biz.business_id}`, { status: next });
      setBusinesses(businesses.map((b) => b.business_id === biz.business_id ? { ...b, status: next } : b));
      toast.success(`Status: ${next}`);
    } catch { toast.error("Failed"); }
  };
  const updateTicket = async (t, status) => {
    try {
      await api.put(`/admin/support/${t.ticket_id}`, { status });
      setTickets(tickets.map((x) => x.ticket_id === t.ticket_id ? { ...x, status } : x));
    } catch { toast.error("Failed"); }
  };

  if (loading || loadingData) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  if (user && !user.is_admin) {
    return (
      <div className="min-h-screen bg-[#FFFDF0] flex items-center justify-center px-6">
        <div className="nb-card p-8 max-w-md text-center" data-testid="admin-access-denied">
          <Shield className="w-8 h-8 mx-auto mb-3 text-[#FF7F50]" />
          <div className="font-display text-xl font-extrabold">Admin access required</div>
          <p className="text-sm text-gray-600 mt-2">
            You are signed in as <b>{user.email}</b>, but this account does not have the admin flag.
          </p>
          <p className="text-xs text-gray-500 mt-3">
            Admin access is granted when your email is listed in the ADMIN_EMAILS environment
            variable <b>at the time you log in</b>. If it was added recently, log out and log back
            in to refresh your access.
          </p>
          <button onClick={() => navigate("/dashboard")} className="mt-4 px-4 py-2 border-2 border-black rounded-xl bg-white font-semibold nb-press" data-testid="admin-denied-back-btn">
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF0]">
      <header className="border-b-2 border-black bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} data-testid="admin-back-btn" className="nb-press p-2 border-2 border-black rounded-lg bg-white nb-shadow-sm"><ArrowLeft className="w-5 h-5" /></button>
            <Shield className="w-6 h-6 text-[#FF7F50]" />
            <div className="font-display text-xl font-extrabold">Admin Dashboard</div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-2 mb-6">
          {[{ id: "users", label: "Users", icon: Users, count: users.length },
            { id: "businesses", label: "Websites", icon: Globe, count: businesses.length },
            { id: "support", label: "Support", icon: MessageCircle, count: tickets.length },
            { id: "config", label: "Config", icon: KeyRound, count: null }].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2.5 rounded-xl border-2 border-black font-semibold flex items-center gap-2 nb-press ${tab === t.id ? "bg-[#111] text-white" : "bg-white"}`}>
              <t.icon className="w-4 h-4" />{t.label}{t.count !== null && <span className="ml-1 text-xs opacity-70">({t.count})</span>}
            </button>
          ))}
        </div>

        {tab === "users" && (
          <div className="nb-card p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F0FF] border-b-2 border-black">
                <tr><th className="text-left p-3 font-bold">Email</th><th className="text-left p-3 font-bold">Name</th><th className="text-left p-3 font-bold">Admin</th><th className="text-left p-3 font-bold">Joined</th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-gray-200" data-testid={`user-row-${u.user_id}`}>
                    <td className="p-3">{u.email}</td>
                    <td className="p-3">{u.name}</td>
                    <td className="p-3">{u.is_admin ? "✓" : ""}</td>
                    <td className="p-3 text-xs text-gray-500">{u.created_at?.toString().slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "businesses" && (
          <div className="space-y-3">
            {businesses.map((b) => (
              <div key={b.business_id} className="nb-card p-4 flex flex-wrap items-center gap-3" data-testid={`biz-row-${b.business_id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-bold">{b.summary?.name || "Untitled"}</div>
                  <div className="text-xs text-gray-500">User: {b.user_id} · Slug: {b.slug}</div>
                  {b.custom_domain && <div className="text-xs text-gray-500">Domain: {b.custom_domain} ({b.domain_status})</div>}
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-bold border-2 border-black ${b.status === "published" ? "bg-[#10B981] text-white" : b.status === "disabled" ? "bg-red-500 text-white" : "bg-gray-200"}`}>{b.status}</span>
                <a href={`/site/${b.slug}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 border-2 border-black rounded-lg bg-white text-xs font-semibold nb-press">View</a>
                <button onClick={() => toggleStatus(b)} data-testid={`toggle-${b.business_id}`} className="px-3 py-1.5 border-2 border-black rounded-lg bg-[#F5F0FF] text-xs font-semibold nb-press">Toggle status</button>
              </div>
            ))}
          </div>
        )}

        {tab === "config" && <WebhookSecretsCard />}

        {tab === "support" && (
          <div className="space-y-3">
            {tickets.map((t) => (
              <div key={t.ticket_id} className="nb-card p-4" data-testid={`tkt-row-${t.ticket_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="font-bold">{t.subject}</div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-xs rounded-full bg-[#F5F0FF] border border-black font-bold">{t.urgency}</span>
                    <select value={t.status} onChange={(e) => updateTicket(t, e.target.value)} className="text-xs border-2 border-black rounded-lg px-2 py-1 bg-white" data-testid={`tkt-status-${t.ticket_id}`}>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-1">{t.user_email}</div>
                <p className="text-sm mt-2">{t.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
